/**
 * Chat Assistant - Main container component
 * Manages chat state and coordinates child components
 */
import React, { useState, useEffect } from 'react';
import { Fab, Badge } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ChatDrawer from './ChatDrawer';
import chatService from '../../services/chatService';
import demoService from '../../services/demoService';

const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Initialize or restore session
  useEffect(() => {
    const storedSessionId = sessionStorage.getItem('chatSessionId');
    if (storedSessionId) {
      setSessionId(storedSessionId);
      loadMessages(storedSessionId);
    }
    loadSuggestions();
  }, []);

  const loadMessages = async (sid) => {
    try {
      const msgs = await chatService.getMessages(sid);
      setMessages(msgs);
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const loadSuggestions = async () => {
    try {
      const sugg = await chatService.getSuggestions();
      setSuggestions(sugg);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setUnreadCount(0);
    // Mark AI chat demo feature as completed
    if (demoService.isDemoMode()) {
      demoService.markFeatureCompleted('ai_chat');
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleStartNewSession = async () => {
    try {
      const response = await chatService.createSession('New conversation');
      setSessionId(response.session_id);
      sessionStorage.setItem('chatSessionId', response.session_id);
      setMessages([]);
      loadSuggestions();
    } catch (error) {
      console.error('Error creating session:', error);
    }
  };

  const handleSendMessage = async (message) => {
    // If no session, create one
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      try {
        const response = await chatService.createSession();
        currentSessionId = response.session_id;
        setSessionId(currentSessionId);
        sessionStorage.setItem('chatSessionId', currentSessionId);
      } catch (error) {
        console.error('Error creating session:', error);
        return;
      }
    }

    // Add user message to UI immediately
    const userMessage = {
      role: 'user',
      content: message,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    try {
      // Send message and get response
      const response = await chatService.sendMessage(currentSessionId, message);
      
      // Add assistant response
      setMessages(prev => [...prev, response]);
      
      // If drawer is closed, increment unread count
      if (!open) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        created_at: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSession = async () => {
    if (sessionId) {
      try {
        await chatService.deleteSession(sessionId);
      } catch (error) {
        console.error('Error deleting session:', error);
      }
    }
    setSessionId(null);
    setMessages([]);
    sessionStorage.removeItem('chatSessionId');
    handleStartNewSession();
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="chat"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: { xs: 72, sm: 24 }, // Above bottom nav on mobile
          right: { xs: 16, sm: 24 },
          zIndex: 1200,
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <ChatIcon />
        </Badge>
      </Fab>

      {/* Chat Drawer */}
      <ChatDrawer
        open={open}
        onClose={handleClose}
        messages={messages}
        loading={loading}
        suggestions={suggestions}
        onSendMessage={handleSendMessage}
        onClearSession={handleClearSession}
        onNewSession={handleStartNewSession}
      />
    </>
  );
};

export default ChatAssistant;
